import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { IftaLabelModule } from 'primeng/iftalabel';
import {
  LucideAngularModule,
  TreePine,
  Sun,
  Zap,
  Flame,
  Wind,
  CookingPot,
  Droplets,
  Recycle,
  Wheat,
  Waves,
  CircleDot,
  Star,
  Pin,
  EyeOff,
  BadgeCheck,
  LucideIconData,
} from 'lucide-angular';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ProjectService } from '../../services/project.service';
import { Project, ProjectStatus, ProjectType, Region, CertificationStandard, ProjectFilters } from '../../models/project.model';

@Component({
  selector: 'app-list-projects',
  imports: [
    CommonModule,
    ButtonModule,
    RouterLink,
    TableModule,
    ToastModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    FormsModule,
    LucideAngularModule,
    IftaLabelModule,
  ],
  templateUrl: './list-projects.component.html',
  styleUrl: './list-projects.component.scss',
})
export class ListProjectsComponent implements OnInit {
  projectService = inject(ProjectService);
  router = inject(Router);
  confirmationService = inject(ConfirmationService);
  notificationService = inject(NotificationService);

  projectList: Project[] = [];
  globalFilter: string = '';

  // Filter options
  statusFilter: ProjectStatus | null = null;
  typeFilter: ProjectType | null = null;
  regionFilter: Region | null = null;
  certificationFilter: CertificationStandard | null = null;

  // Dropdown options
  statusOptions = [
    { label: 'All', value: null },
    { label: 'Draft', value: ProjectStatus.DRAFT },
    { label: 'Active', value: ProjectStatus.ACTIVE },
    { label: 'Inactive', value: ProjectStatus.INACTIVE },
    { label: 'Expired', value: ProjectStatus.EXPIRED },
  ];

  typeOptions = [
    { label: 'All', value: null },
    { label: 'Reforestation', value: ProjectType.REFORESTATION },
    { label: 'Renewable Energy', value: ProjectType.RENEWABLE_ENERGY },
    { label: 'Energy Efficiency', value: ProjectType.ENERGY_EFFICIENCY },
    { label: 'Biochar', value: ProjectType.BIOCHAR },
    { label: 'Direct Air Capture', value: ProjectType.DIRECT_AIR_CAPTURE },
    { label: 'Clean Cookstoves', value: ProjectType.CLEAN_COOKSTOVES },
    { label: 'Water Purification', value: ProjectType.WATER_PURIFICATION },
    { label: 'Waste Management', value: ProjectType.WASTE_MANAGEMENT },
    { label: 'Sustainable Agriculture', value: ProjectType.SUSTAINABLE_AGRICULTURE },
    { label: 'Blue Carbon', value: ProjectType.BLUE_CARBON },
    { label: 'Other', value: ProjectType.OTHER },
  ];

  regionOptions = [
    { label: 'All', value: null },
    { label: 'Africa', value: Region.AFRICA },
    { label: 'Americas', value: Region.AMERICAS },
    { label: 'Asia', value: Region.ASIA },
    { label: 'Europe', value: Region.EUROPE },
    { label: 'Oceania', value: Region.OCEANIA },
    { label: 'Multi-Regional', value: Region.MULTI_REGIONAL },
  ];

  certificationOptions = [
    { label: 'All', value: null },
    { label: 'Gold Standard', value: CertificationStandard.GOLD_STANDARD },
    { label: 'Verra VCS', value: CertificationStandard.VERRA_VCS },
    { label: 'Certified DAC', value: CertificationStandard.CERTIFIED_DAC },
    { label: 'Plan Vivo', value: CertificationStandard.PLAN_VIVO },
    { label: 'Climate Action Reserve', value: CertificationStandard.CLIMATE_ACTION_RESERVE },
    { label: 'American Carbon Registry', value: CertificationStandard.AMERICAN_CARBON_REGISTRY },
    { label: 'Uncertified', value: CertificationStandard.UNCERTIFIED },
    { label: 'Other', value: CertificationStandard.OTHER },
  ];

  ngOnInit(): void {
    this.loadProjects();
  }

  loadProjects(): void {
    const filters: ProjectFilters = {};

    if (this.statusFilter) filters.status = this.statusFilter;
    if (this.typeFilter) filters.projectType = this.typeFilter;
    if (this.regionFilter) filters.region = this.regionFilter;
    if (this.certificationFilter) filters.certificationStandard = this.certificationFilter;

    this.projectService.getAllProjects(filters).subscribe({
      next: (response) => {
        this.projectList = response.data;
      },
      error: (err) => {
        console.error('Error fetching projects:', err);
        this.notificationService.error('Failed to load projects');
      },
    });
  }

  onFilterChange(): void {
    this.loadProjects();
  }

  resetFilters(): void {
    this.globalFilter = '';
    this.statusFilter = null;
    this.typeFilter = null;
    this.regionFilter = null;
    this.certificationFilter = null;
    this.loadProjects();
  }

  editProject(projectId: string): void {
    this.router.navigate(['/admin/projects/edit', projectId]);
  }

  confirmDelete(projectId: string): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this project? This action cannot be undone.',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteProject(projectId),
    });
  }

  deleteProject(projectId: string): void {
    this.projectService.deleteProject(projectId).subscribe({
      next: () => {
        this.projectList = this.projectList.filter((project) => project.id !== projectId);
        this.notificationService.success('Project deleted successfully');
      },
      error: (err) => {
        console.error('Error deleting project:', err);
        this.notificationService.error('Failed to delete project');
      },
    });
  }

  changeStatus(project: Project, newStatus: ProjectStatus): void {
    this.projectService.changeStatus(project.id, newStatus).subscribe({
      next: (response) => {
        const index = this.projectList.findIndex((p) => p.id === project.id);
        if (index !== -1) {
          this.projectList[index] = response.data;
        }
        this.notificationService.success('Project status updated');
      },
      error: (err) => {
        console.error('Error changing status:', err);
        this.notificationService.error('Failed to update status');
      },
    });
  }

  // Lucide icons
  readonly typeIcons: Record<ProjectType, LucideIconData> = {
    [ProjectType.REFORESTATION]: TreePine,
    [ProjectType.RENEWABLE_ENERGY]: Sun,
    [ProjectType.ENERGY_EFFICIENCY]: Zap,
    [ProjectType.BIOCHAR]: Flame,
    [ProjectType.DIRECT_AIR_CAPTURE]: Wind,
    [ProjectType.CLEAN_COOKSTOVES]: CookingPot,
    [ProjectType.WATER_PURIFICATION]: Droplets,
    [ProjectType.WASTE_MANAGEMENT]: Recycle,
    [ProjectType.SUSTAINABLE_AGRICULTURE]: Wheat,
    [ProjectType.BLUE_CARBON]: Waves,
    [ProjectType.OTHER]: CircleDot,
  };

  readonly starIcon = Star;
  readonly pinIcon = Pin;
  readonly eyeOffIcon = EyeOff;
  readonly badgeCheckIcon = BadgeCheck;

  getTypeIcon(type: ProjectType): LucideIconData {
    return this.typeIcons[type] || CircleDot;
  }

  getStatusLabel(status: ProjectStatus): string {
    const labels: Record<ProjectStatus, string> = {
      [ProjectStatus.DRAFT]: 'Draft',
      [ProjectStatus.ACTIVE]: 'Active',
      [ProjectStatus.INACTIVE]: 'Inactive',
      [ProjectStatus.EXPIRED]: 'Expired',
    };
    return labels[status] || status;
  }

  getStatusClasses(status: ProjectStatus): string {
    const classes: Record<ProjectStatus, string> = {
      [ProjectStatus.DRAFT]: 'bg-greyscale-100 text-greyscale-700',
      [ProjectStatus.ACTIVE]: 'bg-validation-100 text-validation-700',
      [ProjectStatus.INACTIVE]: 'bg-warning-100 text-warning-700',
      [ProjectStatus.EXPIRED]: 'bg-error-100 text-error-700',
    };
    return classes[status] || 'bg-greyscale-100 text-greyscale-700';
  }

  getTypeLabel(type: ProjectType): string {
    const labels: Record<ProjectType, string> = {
      [ProjectType.REFORESTATION]: 'Reforestation',
      [ProjectType.RENEWABLE_ENERGY]: 'Renewable Energy',
      [ProjectType.ENERGY_EFFICIENCY]: 'Energy Efficiency',
      [ProjectType.BIOCHAR]: 'Biochar',
      [ProjectType.DIRECT_AIR_CAPTURE]: 'Direct Air Capture',
      [ProjectType.CLEAN_COOKSTOVES]: 'Clean Cookstoves',
      [ProjectType.WATER_PURIFICATION]: 'Water Purification',
      [ProjectType.WASTE_MANAGEMENT]: 'Waste Management',
      [ProjectType.SUSTAINABLE_AGRICULTURE]: 'Sustainable Agriculture',
      [ProjectType.BLUE_CARBON]: 'Blue Carbon',
      [ProjectType.OTHER]: 'Other',
    };
    return labels[type] || type;
  }

  getRegionLabel(region: Region): string {
    const labels: Record<Region, string> = {
      [Region.AFRICA]: 'Africa',
      [Region.AMERICAS]: 'Americas',
      [Region.ASIA]: 'Asia',
      [Region.EUROPE]: 'Europe',
      [Region.OCEANIA]: 'Oceania',
      [Region.MULTI_REGIONAL]: 'Multi-Regional',
    };
    return labels[region] || region;
  }

  getCertificationLabel(cert: CertificationStandard): string {
    const labels: Record<CertificationStandard, string> = {
      [CertificationStandard.GOLD_STANDARD]: 'Gold Standard',
      [CertificationStandard.VERRA_VCS]: 'Verra VCS',
      [CertificationStandard.CERTIFIED_DAC]: 'Certified DAC',
      [CertificationStandard.PLAN_VIVO]: 'Plan Vivo',
      [CertificationStandard.CLIMATE_ACTION_RESERVE]: 'Climate Action Reserve',
      [CertificationStandard.AMERICAN_CARBON_REGISTRY]: 'American Carbon Registry',
      [CertificationStandard.UNCERTIFIED]: 'Uncertified',
      [CertificationStandard.OTHER]: 'Other',
    };
    return labels[cert] || cert;
  }

  formatPrice(price: number | null | undefined): string {
    if (price == null) return 'N/A';
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;
    return `€${priceNum.toFixed(2)}/t`;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      timeZone: 'Europe/Brussels',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}
